import { ArrowDownIcon, TimingIcon } from "@dataone/icons";
import {
  IPaginationFilters,
  ISearchHistory,
  ISearchPayload,
  PaginationComponent,
  QUERIES,
  chartType,
  getRecentSearch,
  getSearchSessionById,
  getSearchSettings,
  openNotification,
} from "@dataone/lib";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Skeleton } from "antd";
import { useState } from "react";
import { styled } from "styled-components";
import { LoadingOutlined } from "@ant-design/icons";

interface PreviousSearchesProps {
  selectedDBId?: string;
  searchRef: React.RefObject<HTMLDivElement>;
  setResponse: React.Dispatch<React.SetStateAction<Partial<ISearchPayload>>>;
}

const PreviousSearches = ({
  selectedDBId,
  searchRef,
  setResponse,
}: PreviousSearchesProps) => {
  const [recentSearchFilters, setRecentSearchFilters] = useState<
    IPaginationFilters<string, ISearchHistory>
  >({
    body: selectedDBId || "",
    pageNo: 0,
  });

  const [searchLoading, setSearchLoading] = useState("");

  const { data: history, isLoading } = useQuery({
    queryKey: [QUERIES.RECENT_SEARCHES, recentSearchFilters],
    queryFn: () => getRecentSearch(recentSearchFilters),
    enabled: !!selectedDBId,
  });

  const { mutate: getSearch } = useMutation(getSearchSessionById);
  const { mutate: getSettings } = useMutation(getSearchSettings);

  const handlePageChange = (pageNo: number) => {
    setRecentSearchFilters((prev) => ({ ...prev, pageNo }));
  };

  const handleSelectSearch = (id: string) => {
    if (searchLoading) return;
    setSearchLoading(id);
    getSearch(id, {
      onSuccess: (data) => {
        setResponse((prev) => ({
          ...prev,
          id: data.searchId,
          data: data.data,
          summary: data.summary,
          settingsId: data.settingsId,
          suggestion: data.suggestions,
          suggestion_question_settings: data.settingsSuggestions,
          query: data.query,
        }));

        if (data?.settingsId)
          getSettings(data.settingsId, {
            onSuccess: (settings) => {
              setSearchLoading("");
              searchRef.current?.scrollTo({
                top: 0,
                left: 0,
                behavior: "smooth",
              });
              setResponse((prev) => ({
                ...prev,
                chartType: settings.chartType ?? chartType.DEFAULT,
                columnName: settings.columnName,
                rows: settings.rows,
                hasLegend: settings.hasLegend,
                colors: settings.colors,
                title: settings.title,
                subTitle: settings.subTitle,
                dataSource: settings.dataSource,
                cardValuePosition: settings.cardValuePosition,
                cardIntroValue: settings.cardIntroValue,
                cardOutroValue: settings.cardOutroValue,
                // default card settings
                cardIntroBold: true,
                cardIntroFontSize: 27,
                cardValueBold: true,
                cardValueColor: "#4974f6",
                cardValueFontSize: 35,
                cardImage:
                  "https://api-preprod.onegate.ai/api/storage/public/20240418093156627_ai2.png",
                // default chart settings
                enableFormattedNumbers: true,
                displayChartLabels: true,
              }));
            },
          });
        else {
          setSearchLoading("");
          searchRef.current?.scrollTo({
            top: 0,
            left: 0,
            behavior: "smooth",
          });
        }
      },
      onError: () => {
        openNotification("Search not found", "info");
      },
    });
  };

  return (
    <StyledPreviousSearches>
      <h5>Previous Searches </h5>
      {isLoading && selectedDBId ? (
        <HistoryLoading>
          <Skeleton.Input active block size="small" />
          <Skeleton.Input active block size="small" />
          <Skeleton.Input active block size="small" />
        </HistoryLoading>
      ) : (
        <>
          {history?.content?.map((search) => (
            <StyledContainer
              key={search.id}
              onClick={() => handleSelectSearch(search.searchId)}
            >
              <span className="timing-icon">
                {searchLoading === search.searchId ? (
                  <LoadingOutlined />
                ) : (
                  <TimingIcon />
                )}
              </span>
              <div className="search-desc">{search.message}</div>
              <div className="dots-icon">
                <div
                  style={{
                    clear: "both",
                    whiteSpace: "nowrap",
                  }}
                >
                  <span className="arrow-icon">
                    {/* {hovered ? <ArrowTopRightIcon /> : <ArrowDownIcon />} */}
                    {/* <ArrowTopRightIcon /> */}
                    <ArrowDownIcon />
                  </span>
                </div>
              </div>
            </StyledContainer>
          ))}
          <div className="pagination">
            <PaginationComponent
              totalItems={history?.totalElements ?? 0}
              itemsPerPage={5}
              currentPage={history?.pageNo ?? 0}
              onPageChange={handlePageChange}
            />
          </div>
        </>
      )}
    </StyledPreviousSearches>
  );
};

export default PreviousSearches;

const StyledPreviousSearches = styled.div`
  h5 {
    color: #040726;
    font-size: 13px;
    font-weight: 700;
    padding: 10px 0px 8px 10px;
    margin: 0px;
  }
  .pagination {
    margin-top: 10px;
  }
`;

const HistoryLoading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 20px;
`;

const StyledContainer = styled.div`
  margin-top: 10px;
  display: flex;
  flex-direction: row;
  padding: 8px 0px;
  cursor: pointer;
  align-items: center;
  display: flex;
  &:hover {
    background: #eff1fd;
    border-radius: 20px;
  }
  .dots-icon {
    margin-right: 15px;
  }
  .timing-icon {
    margin: 0px 10px 0px 20px;
    // display: flex;
    width: 20px;
    display: flex;
  }
  .search-desc {
    color: #4974f6;
    font-size: 14px;
    font-weight: 300;
    flex: 1;
    max-width: 95%;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  .arrow-icon {
    cursor: pointer;
    font-size: 8px;
    display: flex;
    transform: rotate(270deg);
  }

  .ant-popover {
    z-index: 5;
  }
`;
